import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeftIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ssbDetailsBg from '../assets/images/dashboard-hero.png';
import BuildsView from '../components/SimilarlySituatedBuilder/BuildsView';
import DefinePreviewSkeleton from '../components/SimilarlySituatedBuilder/DefinePreviewSkeleton';
import DefineStep from '../components/SimilarlySituatedBuilder/DefineStep';
import OutcomeStep from '../components/SimilarlySituatedBuilder/OutcomeStep';
import ResultsStep from '../components/SimilarlySituatedBuilder/ResultsStep';
import SsbStepHeader from '../components/SimilarlySituatedBuilder/SsbStepHeader';
import SsbStepper from '../components/SimilarlySituatedBuilder/SsbStepper';
import { useCaseWorkspace } from '../hooks/useCaseWorkspace';
import { useBuilderBoxApi } from '../services/api/useBuilderBox.api';
import { useCaseApi } from '../services/api/useCase.api';
import { BuilderBox } from '../types/SimilarlySituated/BuilderBox.type';
import { CaseType } from '../types/SimilarlySituated/CaseType.type.ts';
import { SsbCondition } from '../types/SimilarlySituated/SsbCondition.type';
import { SsbOutcome } from '../types/SimilarlySituated/SsbOutcome.type';
import { StepDefinition } from '../types/SimilarlySituated/StepDefinition.type';
import {
  analysesToOutcomes,
  builderBoxToDefineState,
  defineStateToBuilderBoxPayload,
  hasDefineChanges,
  isAnalysisSelected,
  isSameDefineState,
  matchesSavedAnalyses,
  outcomesToAnalysesPayload,
} from '../utils/ssbBuilderBox';
import { createCaseTypeFromCase, createEmptyCaseType } from '../utils/ssbCondition';
import { SSB_STEP_HEADERS } from '../utils/ssbStepHeaders';

const BUILDS_PER_PAGE = 100;

const RUN_ERROR_FALLBACK = 'Something went wrong. Please try again.';

interface BuilderNavigationState {
  startNewBuild?: boolean;
  openBuildId?: number;
}

function SimilarlySituatedBuilder() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId?: string }>();
  const location = useLocation();
  const { caseData } = useCaseWorkspace();
  const { getBuilderBoxesByCaseId, createBuilderBox } = useCaseApi();
  const { updateBuilderBox, createAnalyses, updateAnalyses } = useBuilderBoxApi();

  const navigationState = location.state as BuilderNavigationState | null;

  const [activeStep, setActiveStep] = useState<StepDefinition>('Define');
  const [view, setView] = useState<'builds' | 'define'>(navigationState?.startNewBuild ? 'define' : 'builds');
  const [activeBuild, setActiveBuild] = useState<BuilderBox | null>(null);
  const [caseTypes, setCaseTypes] = useState<CaseType[]>(
    navigationState?.startNewBuild ? [createCaseTypeFromCase(caseData)] : [],
  );
  const [prevCaseData, setPrevCaseData] = useState(caseData);
  const [boxTraits, setBoxTraits] = useState<SsbCondition[]>([]);
  const [outcomes, setOutcomes] = useState<SsbOutcome[]>([]);
  const [runCount, setRunCount] = useState(0);
  const [pendingNewBuild, setPendingNewBuild] = useState(false);
  const [buildsSort, setBuildsSort] = useState('updated_at:desc');
  const [pendingOpenBuildId, setPendingOpenBuildId] = useState<number | null>(navigationState?.openBuildId ?? null);
  const [buildsQuery, setBuildsQuery] = useState('');
  const [buildsSearchText, setBuildsSearchText] = useState('');
  const [buildsViewOption, setBuildsViewOption] = useState<'list' | 'grid'>('grid');
  const [hasAnyBuilds, setHasAnyBuilds] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [buildName, setBuildName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: buildsResponse, isLoading: isLoadingBuilds } = useQuery({
    queryKey: ['caseBuilderBoxes', caseId, buildsSort, buildsQuery],
    queryFn: () =>
      getBuilderBoxesByCaseId(Number(caseId), {
        page: 1,
        per_page: BUILDS_PER_PAGE,
        sort: buildsSort,
        query: buildsQuery || undefined,
      }),
    enabled: !!caseId,
  });

  const builds = buildsResponse?.data ?? [];

  // only the unfiltered list can say the case has no builds, a search that matches nothing must not
  const listedAnyBuilds = builds.length > 0 || (buildsResponse && !buildsQuery ? false : hasAnyBuilds);
  if (listedAnyBuilds !== hasAnyBuilds) {
    setHasAnyBuilds(listedAnyBuilds);
  }

  const boxHasContent =
    boxTraits.length > 0 || caseTypes.some((caseType) => caseType.conditions.length > 0 || caseType.excepts.length > 0);

  // load first case type for untouched new build
  if (caseData !== prevCaseData) {
    setPrevCaseData(caseData);
    const isUntouched = isSameDefineState(
      { caseTypes, boxTraits },
      { caseTypes: [createCaseTypeFromCase(prevCaseData)], boxTraits: [] },
    );
    if (view === 'define' && !activeBuild && isUntouched) {
      setCaseTypes([createCaseTypeFromCase(caseData)]);
    }
  }

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // remain in builds view on refresh
  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.key, location.state, location.pathname, navigate]);

  const handleBuildsSearchChange = (value: string) => {
    setBuildsSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => setBuildsQuery(value), 500);
  };

  const invalidateBuilds = () =>
    queryClient.invalidateQueries({ queryKey: ['caseBuilderBoxes', caseId], exact: false });

  const { mutate: saveBuild, isPending: isSavingBuild } = useMutation({
    mutationKey: ['saveBuilderBox', caseId, activeBuild?.id],
    mutationFn: (name: string) => {
      const payload = defineStateToBuilderBoxPayload(name, caseTypes, boxTraits);

      // an opened build is updated in place, a new one is created on the case
      return activeBuild
        ? updateBuilderBox(activeBuild.id, { name: payload.name, condition_boxes: payload.condition_boxes })
        : createBuilderBox(Number(caseId), payload);
    },
    onSuccess: (build) => {
      invalidateBuilds();
      queryClient.invalidateQueries({ queryKey: ['builderBoxAnalysisTypes', build.id] });
      setNotice({
        message: `Box "${build.name}" successfully ${activeBuild ? 'updated' : 'created'}.`,
        severity: 'success',
      });
      setActiveBuild(build);
      resetAnalysis();
      setSaveDialogOpen(false);
      setActiveStep('Analyze');
    },
    onError: (error) => {
      const message = error?.message || 'Failed to build the box. Please try again.';
      // a rebuild has no dialog to hold the error
      if (activeBuild) {
        setNotice({ message, severity: 'error' });
      } else {
        setSaveError(message);
      }
    },
  });

  const { mutate: duplicateBuild } = useMutation({
    mutationKey: ['duplicateBuilderBox', caseId],
    mutationFn: async (build: BuilderBox) => {
      const { caseTypes: sourceCaseTypes, boxTraits: sourceBoxTraits } = builderBoxToDefineState(build);
      const copy = await createBuilderBox(
        Number(caseId),
        defineStateToBuilderBoxPayload(`${build.name} (copy)`, sourceCaseTypes, sourceBoxTraits),
      );

      if (!build.analyses?.length) return { copy, outcomesCopied: true };

      // the copy already exists at this point, so a failed outcome copy must not read as a failed duplicate
      try {
        await createAnalyses(copy.id, outcomesToAnalysesPayload(analysesToOutcomes(build.analyses)));
        return { copy, outcomesCopied: true };
      } catch {
        return { copy, outcomesCopied: false };
      }
    },
    onSuccess: ({ copy, outcomesCopied }) => {
      setNotice(
        outcomesCopied
          ? { message: `Box "${copy.name}" successfully created.`, severity: 'success' }
          : {
              message: `Box "${copy.name}" was created, but its outcomes could not be copied.`,
              severity: 'error',
            },
      );
    },
    onError: (error) => {
      setNotice({ message: error?.message || 'Failed to duplicate the box. Please try again.', severity: 'error' });
    },
    onSettled: () => invalidateBuilds(),
  });

  const {
    mutate: runAnalysis,
    isPending: isRunningAnalysis,
    error: runError,
    reset: resetAnalysis,
  } = useMutation({
    mutationKey: ['saveBuilderBoxAnalyses', activeBuild?.id],
    // the first run creates the box analyses, later runs replace them
    mutationFn: ({ build, selected }: { build: BuilderBox; selected: SsbOutcome[] }) => {
      const payload = outcomesToAnalysesPayload(selected);
      return build.analyses?.length ? updateAnalyses(build.id, payload) : createAnalyses(build.id, payload);
    },
    onSuccess: (build, { selected }) => {
      invalidateBuilds();
      setActiveBuild(build);
      // outcomes the catalog dropped are not run, so the selection follows what the build now holds
      setOutcomes(selected);
      showResults();
    },
  });

  const needsRebuild = activeBuild !== null && hasDefineChanges(activeBuild, caseTypes, boxTraits);
  // outcomes are only picked against the box as saved
  const canOpenOutcome = activeBuild !== null && !needsRebuild;

  const savedAnalyses = activeBuild?.analyses ?? [];
  const isSavedSelection = (selection: SsbOutcome[]) =>
    savedAnalyses.length > 0 && matchesSavedAnalyses(savedAnalyses, selection);
  // a rebuild clears the saved results, so they only hold for the box and outcomes as saved
  const hasSavedResults = savedAnalyses.some((analysis) => analysis.analysis_result !== null);
  const canOpenResults = canOpenOutcome && hasSavedResults && isSavedSelection(outcomes);
  // a rebuild re-validates the saved analyses, an invalid one blocks running until it is removed from the selection
  const invalidAnalysis = savedAnalyses.find(
    (analysis) => !analysis.is_valid && isAnalysisSelected(analysis, outcomes),
  );

  const getRunErrorMessage = () => {
    if (runError) return runError.message || RUN_ERROR_FALLBACK;
    if (invalidAnalysis) return invalidAnalysis.error_message || RUN_ERROR_FALLBACK;
    return null;
  };

  const showResults = () => {
    setRunCount((prev) => prev + 1);
    setActiveStep('Results');
  };

  const handleOutcomesChange = (nextOutcomes: SsbOutcome[]) => {
    setOutcomes(nextOutcomes);
    resetAnalysis();
  };

  const handleRun = (selected: SsbOutcome[]) => {
    if (!activeBuild) return;
    // the saved analyses already hold results for this selection, there is nothing to run again
    if (hasSavedResults && isSavedSelection(selected)) {
      showResults();
      return;
    }
    runAnalysis({ build: activeBuild, selected });
  };

  const handleBuild = () => {
    if (!activeBuild) {
      openSaveDialog();
    } else if (needsRebuild) {
      saveBuild(activeBuild.name);
    } else {
      // the saved box already matches the define state, nothing to patch
      setActiveStep('Analyze');
    }
  };

  const openSaveDialog = () => {
    setSaveError('');
    setBuildName('');
    setSaveDialogOpen(true);
  };

  const openDefine = (
    build: BuilderBox | null,
    nextCaseTypes: CaseType[],
    nextBoxTraits: SsbCondition[],
    nextOutcomes: SsbOutcome[],
  ) => {
    setActiveBuild(build);
    setCaseTypes(nextCaseTypes.length > 0 ? nextCaseTypes : [createEmptyCaseType()]);
    setBoxTraits(nextBoxTraits);
    setOutcomes(nextOutcomes);
    resetAnalysis();
    setActiveStep('Define');
    setView('define');
  };

  const openEmptyBuild = () => openDefine(null, [createCaseTypeFromCase(caseData)], [], []);

  const openBuild = (build: BuilderBox) => {
    const { caseTypes: buildCaseTypes, boxTraits: buildTraits } = builderBoxToDefineState(build);
    openDefine(build, buildCaseTypes, buildTraits, analysesToOutcomes(build.analyses ?? []));
  };

  // each similarly situated search from the panel arrives as a fresh navigation to this surface
  const [prevLocationKey, setPrevLocationKey] = useState(location.key);
  if (location.key !== prevLocationKey) {
    setPrevLocationKey(location.key);

    if (navigationState?.openBuildId) {
      setPendingOpenBuildId(navigationState.openBuildId);
    } else if (navigationState?.startNewBuild) {
      // searching again rebuilds the box, so confirm before discarding existing work
      if (view === 'define' && boxHasContent) {
        setPendingNewBuild(true);
      } else {
        openEmptyBuild();
      }
    }
  }

  // a build picked from the Discovery Snapshot panel opens as soon as its list arrives
  if (pendingOpenBuildId !== null) {
    const requestedBuild = builds.find((build) => build.id === pendingOpenBuildId);
    if (requestedBuild) {
      setPendingOpenBuildId(null);
      openBuild(requestedBuild);
    }
  }

  // a case without builds, including one whose last build was just deleted, starts on a new build
  if (caseId && view === 'builds' && buildsResponse && !listedAnyBuilds) {
    openEmptyBuild();
  }

  const handleResetConfirm = () => {
    setPendingNewBuild(false);
    openEmptyBuild();
  };

  const showBuilds = view === 'builds' && hasAnyBuilds;
  const showDefine = view === 'define';
  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        overflowY: 'auto',
      }}
    >
      {caseId && isLoadingBuilds && !hasAnyBuilds ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: '80px' }}>
          <CircularProgress />
        </Box>
      ) : showBuilds ? (
        <BuildsView
          builds={builds}
          isLoading={isLoadingBuilds}
          activeSort={buildsSort}
          searchText={buildsSearchText}
          viewOption={buildsViewOption}
          onSortChange={setBuildsSort}
          onSearchChange={handleBuildsSearchChange}
          onToggleView={() => setBuildsViewOption((prev) => (prev === 'grid' ? 'list' : 'grid'))}
          onOpenBuild={openBuild}
          onAddBuild={openEmptyBuild}
          onDuplicateBuild={duplicateBuild}
          onBuildRenamed={(renamed) => setActiveBuild((prev) => (prev?.id === renamed.id ? renamed : prev))}
          onBuildDeleted={(buildId) => setActiveBuild((prev) => (prev?.id === buildId ? null : prev))}
        />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {hasAnyBuilds ? (
              <Button
                startIcon={<ChevronLeftIcon size={16} />}
                sx={{ color: '#4A5568', fontSize: '13px' }}
                onClick={() => setView('builds')}
              >
                Back to saved boxes
              </Button>
            ) : (
              <Box />
            )}
            <SsbStepper
              activeStep={activeStep}
              enabledSteps={
                canOpenResults ? ['Define', 'Analyze', 'Results'] : canOpenOutcome ? ['Define', 'Analyze'] : ['Define']
              }
              onStepChange={setActiveStep}
            />
          </Box>

          {showDefine && activeStep === 'Results' && canOpenResults ? (
            <ResultsStep
              key={runCount}
              analyses={savedAnalyses}
              boxName={activeBuild?.name ?? ''}
              onRefineBox={() => setActiveStep('Define')}
              onBuildNewBox={openEmptyBuild}
              onBackToCase={() => navigate(caseId ? `/case-details/${caseId}` : '/case-details')}
            />
          ) : showDefine && activeStep === 'Analyze' && canOpenOutcome && activeBuild ? (
            <OutcomeStep
              builderBoxId={activeBuild.id}
              outcomes={outcomes}
              savedAnalyses={savedAnalyses}
              countyId={caseData?.county?.id}
              onOutcomesChange={handleOutcomesChange}
              onRun={handleRun}
              isRunning={isRunningAnalysis}
              runError={getRunErrorMessage()}
            />
          ) : showDefine ? (
            <DefineStep
              caseTypes={caseTypes}
              boxTraits={boxTraits}
              countyId={caseData?.county?.id}
              onCaseTypesChange={setCaseTypes}
              onBoxTraitsChange={setBoxTraits}
              onBuildBox={caseId ? handleBuild : undefined}
              isBuildingBox={isSavingBuild}
            />
          ) : (
            <>
              <SsbStepHeader {...SSB_STEP_HEADERS.Define} />
              <DefinePreviewSkeleton />
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  color: '#718096',
                  paddingBottom: '32px',
                }}
              >
                <img src={ssbDetailsBg} style={{ width: '300px' }} alt='Similarly Situated details' />
                <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1A202C' }}>
                  Similarly Situated details
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>Search details shows up here</Typography>
              </Box>
            </>
          )}
        </>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Name this build</DialogTitle>
        <DialogContent>
          <TextField
            margin='dense'
            label='Build name'
            fullWidth
            variant='standard'
            value={buildName}
            onChange={(event) => setBuildName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && buildName.trim()) {
                saveBuild(buildName.trim());
              }
            }}
          />
          {saveError && (
            <Alert severity='error' sx={{ marginTop: 2 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            disableElevation
            disabled={!buildName.trim() || isSavingBuild}
            onClick={() => saveBuild(buildName.trim())}
          >
            Build
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice !== null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setNotice(null)} severity={notice?.severity} sx={{ width: '100%' }}>
          {notice?.message}
        </Alert>
      </Snackbar>

      <Dialog open={pendingNewBuild} onClose={() => setPendingNewBuild(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Reset comparator box?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            Running a new similarly situated search will reset your current state and create the new build.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingNewBuild(false)}>Cancel</Button>
          <Button variant='contained' color='primary' disableElevation onClick={handleResetConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SimilarlySituatedBuilder;
